/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { AddEditCipherListComponent } from './addEditCipherList/addEditCipherList.component';
import { BaseComponent } from '../../../../../core/components';
import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    Input,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { LanguageService, LoadingService, LogService, ModalService, RbacService } from '../../../../../core/services';
import { Observable, Subscriber } from 'rxjs';
import { SSLCipherListView } from '../../models';
import { SSLCiphersApiService } from '../../http';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-cipher-list',
    templateUrl: './cipherList.component.html',
    styleUrls: ['./cipherList.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class CipherListComponent extends BaseComponent {
    /**
     * Reference to add/edit cipher lists container.
     */
    @ViewChild('addEditCipherListContainer', { read: ViewContainerRef, static: true })
    public addEditCipherListContainer: any;

    /**
     * Store the min cipher security version if component is embedded.
     */
    @Input() set minCipherSecurityVersion(version: string) {
        this._minCipherSecurityVersion = version;
        if (this.addEditCipherListsVisible) {
            this.updateAddEditSecurityVersions();
        }
    }

    /**
     * Store the max cipher version if component is embedded.
     */
    @Input() set maxCipherSecurityVersion(version: string) {
        this._maxCipherSecurityVersion = version;
        if (this.addEditCipherListsVisible) {
            this.updateAddEditSecurityVersions();
        }
    }

    /**
     * Cipher rows from API.
     */
    public tableRows: SSLCipherListView[] = [];

    /**
     * Built-in cipher lists.
     */
    public builtinCiphers: any[] = [];

    /**
     * Store the id of the cipher list to edit.
     */
    public editId: string;

    /**
     * Store visibility state of addEditCipherList page.
     */
    public addEditCipherListsVisible: boolean = false;

    /**
     * Component embedded flag.
     */
    public componentEmbedded: boolean = false;

    /**
     * Store the callback function.
     */
    public callBack: (...args: any[]) => void;

    /**
     * Store button permissions lookup.
     */
    public permissionsLookup: any = {};

    /**
     * Store min/max security versions from parent.
     * @private
     */
    private _minCipherSecurityVersion: string;
    private _maxCipherSecurityVersion: string;

    /**
     * Store component reference.
     * @private
     */
    private componentReference: ComponentRef<any>;

    constructor(
        private cdr: ChangeDetectorRef,
        private languageService: LanguageService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private sslCipherApiService: SSLCiphersApiService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
    ) {
        super();
        this.setUpPageData();
        this.subscribeToModalClose();
        this.registerButtons();
    }

    /**
     * Get min/max cipher security version from parent.
     */
    get minCipherSecurityVersion() {
        return this._minCipherSecurityVersion;
    }

    get maxCipherSecurityVersion() {
        return this._maxCipherSecurityVersion;
    }

    /**
     * Go to add/edit cipher list page or create component instance in wizard.
     * @param row
     * @param duplicate
     */
    public addEditCipherList(row?: any, duplicate?: boolean): void {
        if (this.componentEmbedded) {
            this.createEmbeddedComponent(row, duplicate);
        } else {
            const url: string = duplicate ? 'duplicate/' + row.id : row ? 'edit/' + row.id : 'new';
            this.router.navigate(
                [url],
                { relativeTo: this.route }).then().catch();
        }
    }

    /**
     * Create a new instance of the cipher list page to embed in the wizard.
     * @param row
     * @param duplicate
     * @private
     */
    private createEmbeddedComponent(row?: any, duplicate?: boolean): void {
        const addEditComponent = this.addEditCipherListContainer.createComponent(AddEditCipherListComponent);

        addEditComponent.instance.editId = row ? row.id : '';
        addEditComponent.instance.componentEmbedded = true;
        addEditComponent.instance.addEditPageVisible = true;
        addEditComponent.instance.minCipherSecVersion = this.minCipherSecurityVersion;
        addEditComponent.instance.maxCipherSecVersion = this.maxCipherSecurityVersion;
        addEditComponent.instance.duplicate = duplicate;
        addEditComponent.instance.callBack = this.cancelAddEditCipherPage.bind(this);

        this.componentReference = addEditComponent;
        this.addEditCipherListsVisible = true;
        this.cdr.detectChanges();
    }

    /**
     * Cancel add/edit callback function.
     * @private
     */
    private cancelAddEditCipherPage(): void {
        this.addEditCipherListsVisible = false;
        this.componentReference.destroy();
        this.setUpPageData();
    }

    /**
     * Send updated security versions to the add/edit page.
     * @private
     */
    private updateAddEditSecurityVersions(): void {
        this.componentReference.setInput('minCipherSecVersion', this.minCipherSecurityVersion);
        this.componentReference.setInput('maxCipherSecVersion', this.maxCipherSecurityVersion);
    }

    /**
     * Confirm delete cipher list.
     * @param id
     * @param name
     */
    public confirmDelete(id: string, name: string): void {
        const confirmationObject = {
            title: 'cipher_list_delete_cipher',
            message: 'Do you wish to delete cipher list ' + name,
            callBack: this.deleteCipherList.bind(this, id),
            options: { translateText: false }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete cipher.
     * @param id
     */
    public deleteCipherList(id: string): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.sslCipherApiService.deleteCipherList(id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('CipherListComponent: deleteCipherList', 'failed to delete list', error);
                        observer.error(error);
                        observer.complete();
                    }
                })
                .add((): void => {
                    this.loadingService.loadingActionDotsEmitter.next(false);
                });
        });
    }

    /**
     * View cipher list.
     * @param row
     */
    public viewCipherList(row: any): void {
        this.builtinCiphers = row.ciphers;
        this.modalService.openModalInParent('viewCipherList');
    }

    /**
     * Subscribe to modal close events.
     * @private
     */
    private subscribeToModalClose(): void {
        this.modalService.closeParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (modalId: string): void => {
                    if (modalId === 'viewCipherList') {
                        this.loadingService.loadingActionDotsEmitter.next(false);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('subscribeToModalClose', 'failed to get modal close data' + error);
                }
            });
    }

    /**
     * Get the user cipher list.
     * @private
     */
    private setUpPageData(): void {
        this.sslCipherApiService.getAllCipherLists()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (cipherLists: any[]): void => {
                    this.populateTableRows(cipherLists);
                }
            });
    }

    /**
     * Populate table rows with the cipher list data.
     * @param cipherLists
     * @private
     */
    private populateTableRows(cipherLists: any[]): void {
        this.tableRows = [];
        for (const cipher of cipherLists) {
            const type: string = this.languageService.translate(cipher.builtin ? 'builtin' : 'user');
            this.tableRows.push({
                name: cipher.name,
                builtin: type,
                ciphers: cipher.ciphers,
                id: cipher.id,
                usage: cipher.usage
            });
        }
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'ssl-cipher-lists-new': [{ path: '/ciphers/lists', methods: ['POST'] }],
            'ssl-cipher-lists-edit': [{ path: '/ciphers/lists/:id', methods: ['PUT'] }],
            'ssl-cipher-lists-delete': [{ path: '/ciphers/lists/:id', methods: ['DELETE'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
